import { Box } from '@chakra-ui/react';
import { DateTime } from 'luxon';
import { useCalendar } from './TCalendar.controller';
import CalendarDay from './components/calendar-day/calendar-day.view';

export interface ITTCalendarEvent {
	title: string;
	id: string;
	start: DateTime;
	end: DateTime;
	color: string;
	row?: number;
	/**
	 * Public UI field.
	 * When true the hour is hidden. This is useful for all day events.
	 */
	uiHideHour?: boolean;
}
interface ITCalendarProps {
	onEventClick?: (event: ITTCalendarEvent) => void;
	onRangeSelected?: (args: { start: DateTime; end: DateTime }) => void;
	events: Array<ITTCalendarEvent>;
	isLoading?: boolean;
	locale?: string;
	month: number;
	timeZone: string;
	weekStartsOnMonday?: boolean;
	year: number;
}

export default function TCalendar(props: ITCalendarProps) {
	const locale = props.locale ?? navigator.language;
	const { days, state, dispatch, handleEventClick } = useCalendar({ ...props, locale });

	return (
		<Box display="grid" gridTemplateRows="1fr" w="100%" h="100%" overflow="auto">
			<Box
				// Add a delay animation when opacity changes
				transition="all .2s"
				opacity={props.isLoading ? 0.5 : 1}
				overflow="auto"
				bg="white"
				display="grid"
				gridTemplateColumns={{ base: '1fr', sm: 'repeat(7, 1fr)' }}
				gridTemplateRows={{ base: 'repeat(42, 1fr)', sm: 'repeat(6, 1fr)' }}
				cursor={state.isSelecting ? 'grabbing' : 'default'}
			>
				{days.map((day) => {
					return (
						<CalendarDay
							key={day.plainDate.toString()}
							isDragging={
								!!state.selectionStart &&
								!!state.selectionEnd &&
								day.dt >= state.selectionStart &&
								day.dt <= state.selectionEnd
							}
							onMouseDown={(e) => {
								// If the target is not the data-timetime-element="day" return
								if (e.currentTarget.getAttribute('data-timetime-element') !== 'day') {
									return;
								}
								dispatch({ selectionEnd: day.dt, selectionStart: day.dt, isSelecting: true });
							}}
							onMouseEnter={() => {
								if (state.isSelecting && state.selectionEnd && state.selectionStart) {
									// When entering on a day we need to update the selection
									// If the day is after the end we update the end
									// If the day is before the end but after start we move the end to the left
									// If the day is before the start we update the start
									if (day.dt > state.selectionEnd) {
										dispatch({ selectionEnd: day.dt });
									}
									if (day.dt < state.selectionEnd && day.dt > state.selectionStart) {
										dispatch({ selectionEnd: day.dt });
									}
									if (day.dt < state.selectionStart) {
										dispatch({ selectionStart: day.dt });
									}
								}
							}}
							onMouseUp={(e) => {
								// If the target is not the data-timetime-element="day" return
								if (e.currentTarget.getAttribute('data-timetime-element') !== 'day') {
									return;
								}

								if (!state.selectionStart || !state.selectionEnd) {
									return;
								}

								// The interval goes from the start of the first day to the end of the last day
								props.onRangeSelected?.({
									start: state.selectionStart!.startOf('day'),
									end: state.selectionEnd!.endOf('day'),
								});
								// Reset the state
								dispatch({ isSelecting: false, selectionStart: null, selectionEnd: null });
							}}
							day={day}
							locale={locale}
							timeZone={props.timeZone}
							onEventClick={handleEventClick}
						/>
					);
				})}
			</Box>
		</Box>
	);
}
